import { Button, Card, Image, Spin, Tag } from "antd";
import BanIcon from "components/Icons/BanIcon";
import EditIcon from "components/Icons/EditIcon";
import { statusBadge } from "components/Table";
import { RouteName } from "constants/route";
import { Link } from "react-router-dom";
import ActivateModal from "./components/ActivateModal";
import BanModal from "./components/BanModal";
import CheckIcon from "components/Icons/CheckIcon";
import useFleetModelDetail from "hooks/fleetModels/useFleetModelDetail";
import DeleteIcon from "components/Icons/DeleteIcon";
import DeleteModal from "./components/DeleteModal";
import moment from "moment";

export default function FleetModelDetail() {
  const {
    data: { loading, data },
    handleCloseActivate,
    handleCloseBan,
    handleOpenActivate,
    handleOpenBan,
    handleCloseDelete,
    handleOpenDelete,
    openDelete,
    openActivate,
    openBan,
    fetchData,
  } = useFleetModelDetail();

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "ETB",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number | undefined) => {
    if (value === undefined || value === null) return "N/A";
    return new Intl.NumberFormat("en-US").format(value);
  };

  return (
    <div className="w-full">
      {loading ? (
        <div
          className="w-full bg-white rounded-lg p-6"
          style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
        >
          <div className="w-full flex items-center justify-center py-20">
            <Spin />
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col gap-6">
          {/* Header Card with Image and Basic Info */}
          <Card
            className="w-full"
            style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
          >
            <div className="w-full flex flex-col md:flex-row gap-6">
              {/* Image Section */}
              {data?.image && (
                <div className="w-full md:w-1/3">
                  <Image
                    src={data.image}
                    alt={data.name}
                    className="rounded-lg"
                    style={{ maxHeight: "300px", objectFit: "cover" }}
                    preview={{
                      mask: "Preview",
                    }}
                  />
                </div>
              )}

              {/* Basic Information */}
              <div className="w-full md:w-2/3">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold">{data?.name}</h2>
                  {statusBadge(data?.is_active ?? false)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <span className="text-sm text-gray-500">Seat Number</span>
                    <p className="text-lg font-semibold">{data?.seat_number || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Is Shared</span>
                    <p className="text-lg font-semibold">
                      {data?.is_shared ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="default">No</Tag>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Search Radius</span>
                    <p className="text-lg font-semibold">
                      {data?.search_radius !== null && data?.search_radius !== undefined ? (
                        <>
                          {formatNumber(data.search_radius)} meters
                          {data.search_radius >= 1000 && (
                            <span className="text-sm text-gray-500 ml-2">
                              ({formatNumber(data.search_radius / 1000)} km)
                            </span>
                          )}
                        </>
                      ) : (
                        <Tag color="blue">Using Default from Configuration</Tag>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Model ID</span>
                    <p className="text-sm font-mono text-gray-600">{data?.id}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="w-full flex items-center justify-start gap-x-4 mt-6 pt-6 border-t">
                  <Link to={RouteName.EDIT_FLEET_MODEL.replace(":id", data!.id)}>
                    <Button
                      className="h-auto w-fit flex items-center px-6 py-2 gap-x-1 hover:text-black"
                      type="primary"
                    >
                      <span className="text-sm inline-block">
                        <EditIcon />
                      </span>
                      <span>Edit</span>
                    </Button>
                  </Link>
                  {data?.is_active ? (
                    <Button
                      onClick={handleOpenBan}
                      className="h-auto w-fit flex items-center px-6 py-2 gap-x-1"
                      type="primary"
                      danger
                    >
                      <span className="text-sm inline-block">
                        <BanIcon />
                      </span>
                      <span>Ban</span>
                    </Button>
                  ) : (
                    <Button
                      onClick={handleOpenActivate}
                      className="h-auto w-fit flex items-center px-6 py-2 gap-x-1 bg-success shadow-success shadow-sm hover:shadow-black"
                      type="primary"
                    >
                      <span className="text-sm inline-block">
                        <CheckIcon />
                      </span>
                      <span>Activate</span>
                    </Button>
                  )}
                  <Button
                    onClick={handleOpenDelete}
                    className="h-auto w-fit flex items-center px-6 py-2 gap-x-1"
                    type="primary"
                    danger
                  >
                    <span className="inline-block">
                      <DeleteIcon />
                    </span>
                    <span>Delete</span>
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Pricing Information Cards */}
          <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Standard Pricing */}
            <Card
              title={<span className="font-semibold">Standard Pricing</span>}
              className="w-full"
              style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
            >
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Base Fare</span>
                  <p className="text-xl font-bold text-blue-600">
                    {formatCurrency(data?.base_fare)}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Price Per Kilometer</span>
                  <p className="text-lg font-semibold">
                    {formatCurrency(data?.price_per_kilo_meter)}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Price Per Minute</span>
                  <p className="text-lg font-semibold">
                    {formatCurrency(data?.price_per_minute)}
                  </p>
                </div>
              </div>
            </Card>

            {/* Special Pricing */}
            <Card
              title={<span className="font-semibold">Special Pricing</span>}
              className="w-full"
              style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
            >
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Special Base Fare</span>
                  <p className="text-xl font-bold text-purple-600">
                    {formatCurrency(data?.special_base_fare)}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Special Price Per Kilometer</span>
                  <p className="text-lg font-semibold">
                    {formatCurrency(data?.special_price_per_kilo_meter)}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Special Price Per Minute</span>
                  <p className="text-lg font-semibold">
                    {formatCurrency(data?.special_price_per_minute)}
                  </p>
                </div>
              </div>
            </Card>

            {/* Exceptional Pricing */}
            <Card
              title={<span className="font-semibold">Exceptional Pricing</span>}
              className="w-full"
              style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
            >
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Exceptional Kilometer</span>
                  <p className="text-lg font-semibold">
                    {formatNumber(data?.exceptional_km)} km
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Exceptional Base Fare</span>
                  <p className="text-xl font-bold text-orange-600">
                    {formatCurrency(data?.exceptional_base_fare)}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Exceptional Price Per Kilometer</span>
                  <p className="text-lg font-semibold">
                    {formatCurrency(data?.exceptional_price_per_kilo_meter)}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Exceptional Price Per Minute</span>
                  <p className="text-lg font-semibold">
                    {formatCurrency(data?.exceptional_price_per_minute)}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Additional Information */}
          <Card
            title={<span className="font-semibold">Additional Information</span>}
            className="w-full"
            style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-sm text-gray-500">Created At</span>
                <p className="text-base font-medium">
                  {data?.created_at
                    ? moment(data.created_at).format("MMMM DD, YYYY [at] HH:mm")
                    : "N/A"}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Last Updated</span>
                <p className="text-base font-medium">
                  {data?.updated_at
                    ? moment(data.updated_at).format("MMMM DD, YYYY [at] HH:mm")
                    : "N/A"}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
      {data && (
        <>
          <ActivateModal
            fleetModel={data}
            handleClose={handleCloseActivate}
            open={openActivate}
            reload={fetchData}
          />
          <BanModal
            open={openBan}
            fleetModel={data}
            handleClose={handleCloseBan}
            reload={fetchData}
          />
          <DeleteModal
            open={openDelete}
            fleetModel={data}
            handleClose={handleCloseDelete}
          />
        </>
      )}
    </div>
  );
}
